import { Button } from '@workspace/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@workspace/ui/components/dialog'
import { Textarea } from '@workspace/ui/components/textarea'
import { useEffect, useState } from 'react'

interface FeedbackDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: { message: string; pageUrl: string; userAgent: string }) => Promise<void>
}

export function FeedbackDialog({ open, onOpenChange, onSubmit }: FeedbackDialogProps) {
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')

  useEffect(() => {
    if (status === 'success') {
      const timer = setTimeout(() => {
        onOpenChange(false)
      }, 1500)
      return () => clearTimeout(timer)
    }
  }, [status, onOpenChange])

  useEffect(() => {
    if (!open) {
      setStatus('idle')
      setMessage('')
    }
  }, [open])

  async function handleSubmit() {
    if (!message.trim() || status === 'submitting') return
    setStatus('submitting')
    try {
      await onSubmit({
        message: message.trim(),
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      })
      setStatus('success')
    } catch {
      setStatus('error')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Send Feedback</DialogTitle>
          <DialogDescription>
            Found a bug, have a suggestion, or something feels off? Let us know. All feedback helps
            us improve.
          </DialogDescription>
        </DialogHeader>
        {status === 'success' ? (
          <p className="py-6 text-center text-sm text-green-400">Thanks for your feedback!</p>
        ) : (
          <>
            <div className="py-2">
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's on your mind?"
                textareaSize="sm"
                disabled={status === 'submitting'}
                rows={5}
              />
              {status === 'error' && (
                <p className="mt-2 text-xs text-red-400">Something went wrong. Please try again.</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!message.trim() || status === 'submitting'}>
                {status === 'submitting' ? 'Sending...' : 'Send'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
